/* eslint-disable @stylistic/ts/indent */
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Alert,
    Button,
    Container,
    LoadingOverlay,
    Paper,
    PasswordInput,
    Text,
    TextInput,
    Title,
    useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";
import { handleError } from "src/utils/error";

import { useAuthContext, useCompleteNewPassword, useLogin } from "../../hooks/auth";

export interface LoginFormProps {
    onSuccess?();
}

export const LoginForm = ({ onSuccess }: LoginFormProps) => {
    const { t } = useTranslation(["common"]);

    const theme = useMantineTheme();

    const { isAuthenticating, initResetPassword } = useAuthContext();
    const { login } = useLogin();
    const { completeNewPassword } = useCompleteNewPassword();

    const [step, setStep] = useState<"login" | "pwdreset" | "pwdresetrequested" | "changepwd">("login");

    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");

    const [newPassword, setNewPassword] = useState<string>("");

    const handleErrorType = useCallback((err) => {
        switch (err) {
            case "MFA_SETUP":
                return;
            case "NEW_PASSWORD_REQUIRED":
                setStep("changepwd");
                return;
            case "SELECT_MFA_TYPE":
                return;
            case "SMS_MFA":
                return;
            case "SOFTWARE_TOKEN_MFA":
                return;
        }

        // Past the challenge names handled above, err is the error thrown by
        // Auth.signIn. Surface the specific, actionable reason where it is safe;
        // keep wrong-password / unknown-user generic to avoid user enumeration.
        let message = t("signin_fail_description");
        if (err?.name === "PasswordResetRequiredException") {
            message = t("signin_fail_resetrequired");
        } else if (err?.name === "UserNotConfirmedException") {
            message = t("signin_fail_unconfirmed");
        } else if (["LimitExceededException", "TooManyRequestsException", "TooManyFailedAttemptsException"].includes(err?.name)) {
            message = t("signin_fail_ratelimit");
        }

        showNotification({
            color: "red",
            title: t("signin_fail"),
            message,
        });
    }, [t]);

    const [isReseting, setIsReseting] = useState(false);

    const handleResetPassword = useCallback(async () => {
        setIsReseting(true);

        try {
            await initResetPassword(email);
            setStep("pwdresetrequested");
        } catch (err) {
            handleError(err);
        } finally {
            setIsReseting(false);
        }
    }, [email, initResetPassword]);

    const handleLogin = useCallback(() => {
        login(email, password, {
            onError: handleErrorType,
            onSuccess() {
                showNotification({
                    color: "green",
                    message: t("signin_success"),
                });

                onSuccess?.();
            },
        });
    }, [email, handleErrorType, login, onSuccess, password, t]);

    const handleCompleteNewPassword = useCallback(() => {
        completeNewPassword(newPassword, {
            onSuccess() {
                showNotification({
                    color: "green",
                    message: t("changepassword_changed"),
                });

                setStep("login");
            },
            onError(err) {
                showNotification({
                    color: "red",
                    title: t("changepassword_error"),
                    message: String(err),
                });
            },
        });
    }, [completeNewPassword, newPassword, t]);

    const handleSubmit = useCallback((event) => {
        event?.preventDefault?.();

        switch (step) {
            case "pwdreset":
                handleResetPassword();
                return;
            case "login":
                handleLogin();
                return;
            case "changepwd":
                handleCompleteNewPassword();
                return;
        }
    }, [handleResetPassword, handleCompleteNewPassword, handleLogin, step]);

    return (
        <Container size={420} my={40}>
            <Title
                align="center"
                style={{
                    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
                    fontWeight: 900,
                }}
            >
                {t("signin_title")}
            </Title>
            <Text color="dimmed" size="sm" align="center" mt={5}>
                {t("signin_subtitle")}
            </Text>
            <Paper
                mt={30}
                p={30}
                pb={(step === "pwdresetrequested" || step === "changepwd") ? 30 : 15}
                pos="relative"
                shadow="md"
                radius="md"
                component="form"
                onSubmit={handleSubmit}
                withBorder
            >
                <LoadingOverlay visible={isAuthenticating || isReseting} overlayBlur={2} radius="md" />
                {step === "login" && (
                    <>
                        <TextInput
                            id="email"
                            name="email"
                            type="email"
                            label={t("email")}
                            placeholder={t("email")}
                            value={email}
                            onChange={event =>
                                setEmail(event?.target?.value)}
                        />
                        <PasswordInput
                            mt="md"
                            id="password"
                            name="password"
                            label={t("password")}
                            placeholder={t("password")}
                            value={password}
                            onChange={event =>
                                setPassword(event?.target?.value)}
                        />
                        <Button mt="xl" type="submit" fullWidth>
                            {t("signin")}
                        </Button>
                        <Button
                            mt="xs"
                            color="gray"
                            variant="subtle"
                            onClick={() =>
                                setStep("pwdreset")}
                            fullWidth
                        >
                            {t("resetpassword")}
                        </Button>
                    </>
                )}
                {step === "pwdreset" && (
                    <>
                        <Title mb="sm" order={3} align="center">
                            {t("resetpassword")}
                        </Title>
                        <TextInput
                            id="email"
                            name="email"
                            type="email"
                            label={t("email")}
                            placeholder={t("email")}
                            value={email}
                            onChange={event =>
                                setEmail(event?.target?.value)}
                        />
                        <Button mt="xl" type="submit" fullWidth>
                            {t("resetpassword")}
                        </Button>
                        <Button
                            mt="xs"
                            variant="subtle"
                            onClick={() =>
                                setStep("login")}
                            fullWidth
                        >
                            {t("signin")}
                        </Button>
                    </>
                )}
                {step === "pwdresetrequested" && (
                    <>
                        <Title mb="sm" order={3} align="center">
                            {t("resetpassword")}
                        </Title>
                        <Text mb="lg" align="center">
                            {t("resetpassword_sent")}
                        </Text>
                        <Button
                            mt="xs"
                            onClick={() =>
                                setStep("login")}
                            fullWidth
                        >
                            {t("signin")}
                        </Button>
                    </>
                )}
                {step === "changepwd" && (
                    <>
                        <Alert
                            color="orange"
                            icon={<IconAlertCircle size="1rem" />}
                            title={t("changepassword_challengealert_title")}
                        >
                            {t("changepassword_challengealert_description")}
                        </Alert>
                        <PasswordInput
                            mt="md"
                            id="new-password"
                            name="new-password"
                            label={t("password_new")}
                            placeholder={t("password_new")}
                            value={newPassword}
                            onChange={event =>
                                setNewPassword(event?.target?.value)}
                        />
                        <Button mt="xl" type="submit" fullWidth>
                            {t("changepassword")}
                        </Button>
                    </>
                )}
            </Paper>
        </Container>
    );
};
